// CHEMIN : src/pages/acheteur/Commandes.tsx
// e-Vend Studio — Dashboard acheteur : mes commandes
// Adapté de Commandes.tsx (e-Vend). Session : cookie httpOnly (credentials: 'include').
// API : GET /api/acheteur/:gid/commandes · PUT /commandes/:id/annuler · PUT /commandes/:id/confirmer-reception
//       POST /commandes/:id/retour · POST /commandes/:id/avis

import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = '/api';

interface Article { titre?: string; nom?: string; prix?: number | string; quantite?: number; image_url?: string; image?: string }
interface Commande {
  id: number;
  numero_commande: string;
  statut: 'en_attente' | 'confirmee' | 'expediee' | 'livree' | 'annulee';
  statut_paiement: string;
  total: number | string;
  articles: Article[] | null;
  adresse_livraison: Record<string, any> | null;
  note: string | null;
  date_commande: string;
  numero_suivi: string | null;
  transporteur: string | null;
  recu_le: string | null;
  annule_par: string | null;
  raison_annulation: string | null;
  collaborateur_id: number | null;
  boutique: string | null;
  retour_statut: string | null;
  a_avis: boolean;
}

interface Props {
  gestionnaireId: number;
  isMobile?: boolean;
  couleurAccent?: string;
  onEcrire?: (collaborateurId: number) => void;
}

const C = { border: 'rgba(255,255,255,0.08)', textLight: 'rgba(255,255,255,0.5)', cardBg: 'rgba(255,255,255,0.04)', green: '#10b981', red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6' };

const STATUTS: Record<string, { label: string; couleur: string; icone: string }> = {
  en_attente: { label: 'En attente', couleur: '#f59e0b', icone: '⏳' },
  confirmee:  { label: 'Confirmée',  couleur: '#3b82f6', icone: '✔️' },
  expediee:   { label: 'Expédiée',   couleur: '#8b5cf6', icone: '🚚' },
  livree:     { label: 'Livrée',     couleur: '#10b981', icone: '✅' },
  annulee:    { label: 'Annulée',    couleur: '#ef4444', icone: '✖️' },
};
const ETAPES = ['en_attente', 'confirmee', 'expediee', 'livree'];

const RAISONS: { id: string; label: string }[] = [
  { id: 'non_recu', label: 'Article non reçu' }, { id: 'non_conforme', label: 'Non conforme à la description' },
  { id: 'endommage', label: 'Arrivé endommagé' }, { id: 'pieces_manquantes', label: 'Pièces manquantes' },
  { id: 'defectueux', label: 'Produit défectueux' }, { id: 'mauvaise_taille', label: 'Mauvaise taille' },
  { id: 'erreur_commande', label: 'Erreur de commande' }, { id: 'change_idee', label: "J'ai changé d'idée" },
];

const num = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const nomArticle = (a: Article) => a.titre || a.nom || 'Article';

function Pastille({ statut }: { statut: string }) {
  const s = STATUTS[statut] || { label: statut, couleur: '#6b7280', icone: '•' };
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${s.couleur}22`, border: `1px solid ${s.couleur}66`, color: s.couleur, whiteSpace: 'nowrap' }}>{s.icone} {s.label}</span>;
}

export default function Commandes({ gestionnaireId, isMobile = false, couleurAccent = '#fbbf24', onEcrire }: Props) {
  const [liste, setListe] = useState<Commande[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('toutes');
  const [recherche, setRecherche] = useState('');
  const [ouverte, setOuverte] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [modal, setModal] = useState<null | { type: 'annuler' | 'retour' | 'avis'; cmd: Commande }>(null);
  const [raisonAnnul, setRaisonAnnul] = useState('');
  const [typeDemande, setTypeDemande] = useState<'retour' | 'echange' | 'remboursement'>('retour');
  const [raisons, setRaisons] = useState<string[]>([]);
  const [details, setDetails] = useState('');
  const [note, setNote] = useState(5);
  const [titreAvis, setTitreAvis] = useState('');
  const [commentaire, setCommentaire] = useState('');

  const base = `${API}/acheteur/${gestionnaireId}`;
  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${base}/commandes`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Chargement impossible');
      setListe(j.commandes || []);
      setErreur('');
    } catch (e: any) { setErreur(e.message || 'Erreur réseau'); }
    finally { setLoading(false); }
  }, [base]);

  useEffect(() => { charger(); }, [charger]);

  const appeler = async (url: string, method: string, body: any | undefined, ok: string) => {
    setOccupe(true);
    try {
      const r = await fetch(`${base}${url}`, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Action impossible');
      showToast(ok);
      setModal(null);
      await charger();
    } catch (e: any) { showToast(e.message || 'Action impossible', false); }
    finally { setOccupe(false); }
  };

  const ouvrirModal = (type: 'annuler' | 'retour' | 'avis', cmd: Commande) => {
    setRaisonAnnul(''); setTypeDemande('retour'); setRaisons([]); setDetails(''); setNote(5); setTitreAvis(''); setCommentaire('');
    setModal({ type, cmd });
  };

  const kpis = useMemo(() => ({
    total: liste.length,
    enCours: liste.filter(c => ['en_attente', 'confirmee', 'expediee'].includes(c.statut)).length,
    livrees: liste.filter(c => c.statut === 'livree').length,
    depense: liste.filter(c => c.statut !== 'annulee').reduce((s, c) => s + num(c.total), 0),
  }), [liste]);

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return liste.filter(c => (filtre === 'toutes' || c.statut === filtre)
      && (!q || c.numero_commande.toLowerCase().includes(q) || (c.boutique || '').toLowerCase().includes(q) || (c.articles || []).some(a => nomArticle(a).toLowerCase().includes(q))));
  }, [liste, filtre, recherche]);

  const champ: React.CSSProperties = { width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,.06)', border: `1px solid ${C.border}`, borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  const bouton = (fond: string, actif = true): React.CSSProperties => ({ padding: '9px 14px', borderRadius: 10, border: 'none', background: actif ? fond : 'rgba(255,255,255,.08)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: actif ? 'pointer' : 'not-allowed', fontFamily: 'inherit' });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textLight }}>Chargement de vos commandes…</div>;

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      {toast && <div style={{ position: 'fixed', top: 76, right: 16, left: isMobile ? 16 : undefined, background: toast.ok ? C.green : C.red, color: '#fff', padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, zIndex: 3000, boxShadow: '0 4px 16px rgba(0,0,0,.4)' }}>{toast.msg}</div>}

      <div style={{ background: 'linear-gradient(135deg,#3b82f6 0%,#6366f1 100%)', borderRadius: 18, padding: isMobile ? '18px 16px' : '24px 28px', marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontFamily: "'Sora',sans-serif", fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#fff' }}>📦 Mes commandes</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,.85)' }}>Suivez, annulez ou retournez vos achats</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 14, marginBottom: 18 }}>
        {[{ l: 'Commandes', v: kpis.total, c: '#3b82f6' }, { l: 'En cours', v: kpis.enCours, c: '#8b5cf6' }, { l: 'Livrées', v: kpis.livrees, c: '#10b981' }, { l: 'Dépensé', v: `${kpis.depense.toFixed(2)} $`, c: couleurAccent }].map(k => (
          <div key={k.l} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px' }}>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{k.l}</div>
          </div>
        ))}
      </div>

      {erreur && <div style={{ background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.35)', color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{erreur}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input value={recherche} onChange={e => setRecherche(e.target.value)} placeholder="🔍 N° de commande, boutique, produit…" style={{ ...champ, flex: '1 1 220px', width: 'auto' }} />
        <select value={filtre} onChange={e => setFiltre(e.target.value)} style={{ ...champ, width: 'auto', background: '#111c33' }}>
          <option value="toutes">Tous les statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {filtrees.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 18 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>📭</div>
          <p style={{ margin: 0, fontSize: 14, color: C.textLight }}>{liste.length === 0 ? "Vous n'avez pas encore passé de commande." : 'Aucune commande ne correspond à ce filtre.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrees.map(c => {
            const open = ouverte === c.id;
            const etape = ETAPES.indexOf(c.statut);
            const peutAnnuler = (c.statut === 'en_attente' || c.statut === 'confirmee') && c.statut_paiement !== 'paye';
            const peutRetour = (c.statut === 'expediee' || c.statut === 'livree') && !(c.retour_statut === 'en_attente' || c.retour_statut === 'approuve');
            const adr = c.adresse_livraison || {};
            return (
              <div key={c.id} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
                <div onClick={() => setOuverte(open ? null : c.id)} style={{ padding: isMobile ? '12px' : '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#fff' }}>{c.numero_commande}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: C.textLight }}>{fmtDate(c.date_commande)}{c.boutique ? ` · 🏪 ${c.boutique}` : ''}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Pastille statut={c.statut} />
                    {c.retour_statut && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: 'rgba(245,158,11,.15)', color: '#fbbf24' }}>🔄 Retour : {c.retour_statut.replace('_', ' ')}</span>}
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{num(c.total).toFixed(2)} $</span>
                    <span style={{ color: C.textLight, fontSize: 12 }}>{open ? '▲' : '▼'}</span>
                  </div>
                </div>

                {open && (
                  <div style={{ padding: isMobile ? '0 12px 14px' : '0 18px 18px', borderTop: `1px solid ${C.border}` }}>
                    {c.statut !== 'annulee' ? (
                      <div style={{ display: 'flex', alignItems: 'center', margin: '18px 4px 6px' }}>
                        {ETAPES.map((e, i) => (
                          <React.Fragment key={e}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 }}>
                              <div style={{ width: 24, height: 24, borderRadius: '50%', background: i <= etape ? couleurAccent : 'rgba(255,255,255,.1)', color: '#000', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i <= etape ? '✓' : ''}</div>
                              <span style={{ fontSize: 9, color: i <= etape ? '#fff' : C.textLight, textAlign: 'center' }}>{STATUTS[e].label}</span>
                            </div>
                            {i < ETAPES.length - 1 && <div style={{ flex: 1, height: 2, background: i < etape ? couleurAccent : 'rgba(255,255,255,.1)', margin: '0 4px 14px' }} />}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <p style={{ margin: '14px 0 6px', fontSize: 12, color: '#fca5a5' }}>
                        Annulée{c.annule_par === 'acheteur' ? ' par vous' : ''}{c.raison_annulation ? ` — ${c.raison_annulation}` : ''}
                      </p>
                    )}

                    <div style={{ margin: '12px 0' }}>
                      {(c.articles || []).map((a, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,.06)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {(a.image_url || a.image) ? <img src={a.image_url || a.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🛍️'}
                          </div>
                          <span style={{ flex: 1, fontSize: 13, color: '#fff', minWidth: 0, wordBreak: 'break-word' }}>{nomArticle(a)} <span style={{ color: C.textLight }}>× {a.quantite || 1}</span></span>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{(num(a.prix) * (a.quantite || 1)).toFixed(2)} $</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, fontSize: 12, color: 'rgba(255,255,255,.75)' }}>
                      {(adr.ligne1 || adr.adresse || adr.ville) && (
                        <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 10, padding: 12 }}>
                          <b style={{ color: '#fff' }}>📍 Livraison</b><br />
                          {[adr.nom, adr.ligne1 || adr.adresse, adr.ville, adr.province, adr.code_postal].filter(Boolean).join(', ')}
                        </div>
                      )}
                      <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: 10, padding: 12 }}>
                        <b style={{ color: '#fff' }}>💳 Paiement</b><br />{c.statut_paiement === 'paye' ? 'Payé' : c.statut_paiement === 'rembourse' ? 'Remboursé' : 'En attente de paiement'}
                        {c.numero_suivi && <><br /><b style={{ color: '#fff' }}>🚚 Suivi</b> {c.transporteur ? `${c.transporteur} · ` : ''}{c.numero_suivi}</>}
                        {c.recu_le && <><br />Reçue le {fmtDate(c.recu_le)}</>}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                      {c.statut === 'expediee' && <button style={bouton(`linear-gradient(135deg,${C.green},#059669)`)} onClick={() => appeler(`/commandes/${c.id}/confirmer-reception`, 'PUT', undefined, '✅ Réception confirmée')} disabled={occupe}>📬 J'ai reçu ma commande</button>}
                      {peutAnnuler && <button style={bouton('linear-gradient(135deg,#ef4444,#dc2626)')} onClick={() => ouvrirModal('annuler', c)}>✖️ Annuler</button>}
                      {peutRetour && <button style={bouton('linear-gradient(135deg,#f59e0b,#d97706)')} onClick={() => ouvrirModal('retour', c)}>🔄 Retour / remboursement</button>}
                      {c.statut === 'livree' && !c.a_avis && <button style={bouton('linear-gradient(135deg,#8b5cf6,#6366f1)')} onClick={() => ouvrirModal('avis', c)}>⭐ Donner un avis</button>}
                      {c.collaborateur_id && onEcrire && <button style={{ ...bouton('rgba(255,255,255,.1)'), border: `1px solid ${C.border}` }} onClick={() => onEcrire(c.collaborateur_id!)}>💬 Écrire au collaborateur</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modales ── */}
      {modal && (
        <div onClick={e => { if (e.target === e.currentTarget && !occupe) setModal(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', background: '#0f1a30', border: `1px solid ${C.border}`, borderRadius: 18, padding: 22 }}>
            {modal.type === 'annuler' && (
              <>
                <h3 style={{ margin: '0 0 6px', fontSize: 17, fontFamily: "'Sora',sans-serif" }}>Annuler {modal.cmd.numero_commande}</h3>
                <p style={{ margin: '0 0 14px', fontSize: 12, color: C.textLight }}>Cette action est définitive.</p>
                <textarea value={raisonAnnul} maxLength={300} onChange={e => setRaisonAnnul(e.target.value)} rows={3} placeholder="Raison (facultatif)" style={{ ...champ, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={{ ...bouton('rgba(255,255,255,.1)'), flex: 1 }} onClick={() => setModal(null)} disabled={occupe}>Garder la commande</button>
                  <button style={{ ...bouton('#ef4444', !occupe), flex: 1 }} disabled={occupe} onClick={() => appeler(`/commandes/${modal.cmd.id}/annuler`, 'PUT', { raison: raisonAnnul }, 'Commande annulée')}>{occupe ? '⏳…' : 'Annuler la commande'}</button>
                </div>
              </>
            )}
            {modal.type === 'retour' && (
              <>
                <h3 style={{ margin: '0 0 14px', fontSize: 17, fontFamily: "'Sora',sans-serif" }}>Demande pour {modal.cmd.numero_commande}</h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  {(['retour', 'echange', 'remboursement'] as const).map(t => (
                    <button key={t} onClick={() => setTypeDemande(t)} style={{ flex: 1, padding: '9px 4px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${typeDemande === t ? couleurAccent : C.border}`, background: typeDemande === t ? `${couleurAccent}22` : 'rgba(255,255,255,.04)', color: typeDemande === t ? couleurAccent : '#fff' }}>
                      {t === 'retour' ? 'Retour' : t === 'echange' ? 'Échange' : 'Remboursement'}
                    </button>
                  ))}
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: C.textLight }}>RAISONS *</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                  {RAISONS.map(r => (
                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={raisons.includes(r.id)} onChange={() => setRaisons(p => (p.includes(r.id) ? p.filter(x => x !== r.id) : [...p, r.id]))} /> {r.label}
                    </label>
                  ))}
                </div>
                <textarea value={details} maxLength={1000} onChange={e => setDetails(e.target.value)} rows={3} placeholder="Détails (facultatif)" style={{ ...champ, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={{ ...bouton('rgba(255,255,255,.1)'), flex: 1 }} onClick={() => setModal(null)} disabled={occupe}>Fermer</button>
                  <button style={{ ...bouton('linear-gradient(135deg,#f59e0b,#d97706)', raisons.length > 0 && !occupe), flex: 1 }} disabled={!raisons.length || occupe}
                    onClick={() => appeler(`/commandes/${modal.cmd.id}/retour`, 'POST', { type_demande: typeDemande, raisons, details }, '📨 Demande envoyée')}>{occupe ? '⏳…' : 'Envoyer la demande'}</button>
                </div>
              </>
            )}
            {modal.type === 'avis' && (
              <>
                <h3 style={{ margin: '0 0 14px', fontSize: 17, fontFamily: "'Sora',sans-serif" }}>Votre avis sur {modal.cmd.boutique || 'cette boutique'}</h3>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {[1, 2, 3, 4, 5].map(n => <button key={n} onClick={() => setNote(n)} style={{ background: 'none', border: 'none', fontSize: 30, cursor: 'pointer', color: n <= note ? '#fbbf24' : 'rgba(255,255,255,.2)', padding: 0 }}>★</button>)}
                </div>
                <input value={titreAvis} maxLength={120} onChange={e => setTitreAvis(e.target.value)} placeholder="Titre (facultatif)" style={{ ...champ, marginBottom: 10 }} />
                <textarea value={commentaire} maxLength={1500} onChange={e => setCommentaire(e.target.value)} rows={4} placeholder="Votre commentaire…" style={{ ...champ, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={{ ...bouton('rgba(255,255,255,.1)'), flex: 1 }} onClick={() => setModal(null)} disabled={occupe}>Fermer</button>
                  <button style={{ ...bouton('linear-gradient(135deg,#8b5cf6,#6366f1)', !occupe), flex: 1 }} disabled={occupe}
                    onClick={() => appeler(`/commandes/${modal.cmd.id}/avis`, 'POST', { note, titre: titreAvis, commentaire }, '⭐ Merci pour votre avis')}>{occupe ? '⏳…' : 'Publier'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}